import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Input,
  Button,
  Space,
  Typography,
  Modal,
  Select,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tooltip,
  Pagination,
  Checkbox,
  Form,
  Dropdown,
} from 'antd';
import {
  SearchOutlined,
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileOutlined,
  FolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  MoreOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { formatBytes, getFileIcon, getFileTypeFromMime, isSystemFile, getMimeTypeFromFilename } from '../../utils/fileUtils';
import { fileManagementApi } from '../../lib/fileManagementApi';
import FilePreview from '../../components/FilePreview';
import FileUpload from '../../components/FileUpload';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Constant for finetuning folder prefix
const FINETUNING_PREFIX = 'finetuning/';

interface FileItem {
  id: string;
  key: string;
  bucket: string;
  filename: string;
  contentType: string;
  size: number;
  etag: string;
  uploadedAt: string;
  lastAccessed: string;
  isPublic: boolean;
  isSystemFile?: boolean;
  metadata?: Record<string, any>;
  organization?: any;
  uploadedBy?: any;
  url?: string;
}

interface BucketItem {
  Name: string;
  CreationDate: string;
  public?: boolean;
  fileCount?: number;
  totalSize?: number;
}

const TrainingFileStoragePage: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [buckets, setBuckets] = useState<BucketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [bucketModalVisible, setBucketModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('uploadedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showSystemFiles, setShowSystemFiles] = useState<boolean>(false);

  useEffect(() => {
    fetchBuckets();
  }, []);

  useEffect(() => {
    if (selectedBucket) {
      fetchFiles();
    }
  }, [selectedBucket, currentPage, pageSize, searchText, filterType, sortBy, sortOrder, showSystemFiles]);

  const fetchBuckets = async () => {
    try {
      setLoading(true);
      const response = await fileManagementApi.listBuckets();
      setBuckets(response.data.Buckets || []);
      if (response.data.Buckets?.length > 0 && !selectedBucket) {
        setSelectedBucket(response.data.Buckets[0].Name);
      }
    } catch (error) {
      message.error('Failed to fetch buckets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    if (!selectedBucket) return;

    try {
      setLoading(true);
      const params: any = {
        prefix: FINETUNING_PREFIX + (searchText || ''), // Always use finetuning prefix
        delimiter: '/',
        maxKeys: pageSize.toString(),
        continuationToken: currentPage > 1 ? ((currentPage - 1) * pageSize).toString() : undefined,
      };

      if (searchText) {
        // If searching, don't use delimiter to search all levels within finetuning
        delete params.delimiter;
      }

      const response = await fileManagementApi.listObjects(selectedBucket, params);
      
      let filteredFiles = response.data.Contents || [];
      
      // Filter out system files (.metadata, .DS_Store, etc.) unless user wants to see them
      if (!showSystemFiles) {
        filteredFiles = filteredFiles.filter((file: any) => {
          const filename = file.Key.split('/').pop() || file.Key;
          return !isSystemFile(filename);
        });
      }
      
      // Apply file type filter
      if (filterType !== 'all') {
        filteredFiles = filteredFiles.filter((file: any) => {
          const fileType = getFileTypeFromMime(file.contentType);
          return fileType === filterType;
        });
      }

      // Apply sorting
      filteredFiles.sort((a: any, b: any) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];
        const comparison = aValue > bValue ? 1 : -1;
        return sortOrder === 'desc' ? -comparison : comparison;
      });

      setFiles(filteredFiles.map((file: any) => {
        const filename = file.Key.split('/').pop() || file.Key;
        return {
          id: file.Key,
          key: file.Key,
          bucket: selectedBucket,
          filename,
          contentType: file.ContentType || getMimeTypeFromFilename(filename),
          size: file.Size || 0,
          etag: file.ETag?.replace(/"/g, '') || '',
          uploadedAt: file.LastModified || new Date().toISOString(),
          lastAccessed: file.LastModified || new Date().toISOString(),
          isPublic: false,
          isSystemFile: isSystemFile(filename),
          url: `/api/v1/file-management/buckets/${selectedBucket}/objects/${file.Key}`,
        };
      }));

      setTotal(response.data.Contents?.length || 0);
    } catch (error) {
      message.error('Failed to fetch files');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileKey: string) => {
    try {
      await fileManagementApi.deleteFile(selectedBucket, fileKey);
      message.success('File deleted successfully');
      fetchFiles();
      setSelectedFiles(selectedFiles.filter(key => key !== fileKey));
    } catch (error) {
      message.error('Failed to delete file');
      console.error(error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) {
      message.warning('Please select files to delete');
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        selectedFiles.map(fileKey =>
          fileManagementApi.deleteFile(selectedBucket, fileKey)
        )
      );
      message.success(`${selectedFiles.length} files deleted successfully`);
      setSelectedFiles([]);
      fetchFiles();
    } catch (error) {
      message.error('Failed to delete files');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (file: FileItem) => {
    setPreviewFile(file);
    setPreviewModalVisible(true);
  };

  const handleCreateBucket = async (values: { name: string; public: boolean }) => {
    try {
      await fileManagementApi.createBucket({
        name: values.name,
        public: values.public,
      });
      message.success('Bucket created successfully');
      setBucketModalVisible(false);
      fetchBuckets();
    } catch (error) {
      message.error('Failed to create bucket');
      console.error(error);
    }
  };

  const getBucketFileCount = async (bucketName: string): Promise<number> => {
    try {
      const response = await fileManagementApi.listObjects(bucketName, { maxKeys: '1000', prefix: FINETUNING_PREFIX });
      return response.data.Contents?.length || 0;
    } catch (error) {
      console.error('Failed to get bucket file count:', error);
      return 0;
    }
  };

  const handleDeleteBucket = async () => {
    if (!selectedBucket) {
      message.warning('Please select a bucket to delete');
      return;
    }

    try {
      const fileCount = await getBucketFileCount(selectedBucket);
      
      Modal.confirm({
        title: 'Delete Bucket',
        content: fileCount > 0 
          ? `Are you sure you want to delete bucket "${selectedBucket}"? This bucket contains ${fileCount} finetuning file${fileCount !== 1 ? 's' : ''} and all data will be permanently lost.`
          : `Are you sure you want to delete bucket "${selectedBucket}"? This action cannot be undone.`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await fileManagementApi.deleteBucket(selectedBucket);
            message.success('Bucket deleted successfully');
            
            // Reset selected bucket and fetch updated list
            setSelectedBucket('');
            setFiles([]);
            fetchBuckets();
          } catch (error) {
            message.error('Failed to delete bucket');
            console.error(error);
          }
        },
      });
    } catch (error) {
      message.error('Failed to get bucket information');
      console.error(error);
    }
  };

  const handleDownload = async (file: FileItem) => {
    try {
      const response = await fileManagementApi.downloadFile(file.bucket, file.key);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Failed to download file');
      console.error(error);
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedFiles.length === files.length && files.length > 0}
          indeterminate={selectedFiles.length > 0 && selectedFiles.length < files.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedFiles(files.map(f => f.key));
            } else {
              setSelectedFiles([]);
            }
          }}
        />
      ),
      width: 50,
      render: (_: any, record: FileItem) => (
        <Checkbox
          checked={selectedFiles.includes(record.key)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedFiles([...selectedFiles, record.key]);
            } else {
              setSelectedFiles(selectedFiles.filter(key => key !== record.key));
            }
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'filename',
      key: 'filename',
      sorter: true,
      render: (text: string, record: FileItem) => (
        <Space>
          {getFileIcon(record.contentType)}
          <Text strong style={{ opacity: record.isSystemFile ? 0.6 : 1 }}>{text}</Text>
          {record.isPublic && <Tag color="green">Public</Tag>}
          {record.isSystemFile && <Tag color="orange">System</Tag>}
        </Space>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      sorter: true,
      width: 120,
      render: (size: number) => formatBytes(size),
    },
    {
      title: 'Type',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 150,
      render: (contentType: string) => (
        <Tag>{getFileTypeFromMime(contentType)}</Tag>
      ),
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      sorter: true,
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: FileItem) => (
        <Space>
          <Tooltip title="Preview">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this file?"
            onConfirm={() => handleDelete(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fileTypeOptions = [
    { label: 'All Files', value: 'all' },
    { label: 'Images', value: 'image' },
    { label: 'Documents', value: 'document' },
    { label: 'Videos', value: 'video' },
    { label: 'Audio', value: 'audio' },
    { label: 'Archives', value: 'archive' },
    { label: 'Other', value: 'other' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  Fine-tuning Files
                </Title>
                <Text type="secondary">
                  Manage files for your LLM fine-tuning projects (stored in finetuning folder)
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Tooltip 
                    title={
                      <div>
                        <strong>About file storage:</strong><br/>
                        • All files are stored with 'finetuning/' prefix<br/>
                        • Files are organized separately from other application files<br/>
                        • Supported formats: JSONL, CSV, TXT, and more
                      </div>
                    }
                    placement="bottomLeft"
                  >
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<InfoCircleOutlined />}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      About file storage
                    </Button>
                  </Tooltip>
                </div>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setBucketModalVisible(true)}
                  >
                    Create Bucket
                  </Button>
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={() => setUploadModalVisible(true)}
                    disabled={!selectedBucket}
                  >
                    Upload Files
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Files"
              value={total}
              prefix={<FileOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Buckets"
              value={buckets.length}
              prefix={<FolderOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Selected Files"
              value={selectedFiles.length}
              prefix={<Checkbox />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Select
              placeholder="Select Bucket"
              value={selectedBucket}
              onChange={setSelectedBucket}
              style={{ width: '100%' }}
              loading={loading}
            >
              {buckets.map(bucket => (
                <Option key={bucket.Name} value={bucket.Name}>
                  <Space>
                    <FolderOutlined />
                    {bucket.Name}
                    {bucket.public && <Tag color="green">Public</Tag>}
                  </Space>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Search
              placeholder="Search files..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={fetchFiles}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="File Type"
              value={filterType}
              onChange={setFilterType}
              style={{ width: '100%' }}
            >
              {fileTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Sort By"
              value={`${sortBy}-${sortOrder}`}
              onChange={(value) => {
                const [field, order] = value.split('-');
                setSortBy(field);
                setSortOrder(order as 'asc' | 'desc');
              }}
              style={{ width: '100%' }}
            >
              <Option value="filename-asc">Name (A-Z)</Option>
              <Option value="filename-desc">Name (Z-A)</Option>
              <Option value="size-asc">Size (Small)</Option>
              <Option value="size-desc">Size (Large)</Option>
              <Option value="uploadedAt-desc">Date (Newest)</Option>
              <Option value="uploadedAt-asc">Date (Oldest)</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Space>
              {selectedFiles.length > 0 && (
                <Popconfirm
                  title={`Are you sure you want to delete ${selectedFiles.length} files?`}
                  onConfirm={handleBulkDelete}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Delete Selected
                  </Button>
                </Popconfirm>
              )}
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'refresh',
                      icon: <ReloadOutlined />,
                      label: 'Refresh',
                      onClick: fetchFiles,
                    },
                    {
                      key: 'toggleSystemFiles',
                      icon: <InfoCircleOutlined />,
                      label: showSystemFiles ? 'Hide System Files' : 'Show System Files',
                      onClick: () => setShowSystemFiles(!showSystemFiles),
                    },
                    {
                      type: 'divider',
                    },
                    {
                      key: 'deleteBucket',
                      icon: <DeleteOutlined />,
                      label: 'Delete Bucket',
                      onClick: handleDeleteBucket,
                      danger: true,
                      disabled: !selectedBucket,
                    },
                  ],
                }}
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={files}
          rowKey="key"
          loading={loading}
          pagination={false}
          size="small"
        />

        <Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
          <Col>
            <Text type="secondary">
              Showing {files.length} files
              {selectedFiles.length > 0 && ` (${selectedFiles.length} selected)`}
            </Text>
          </Col>
          <Col>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={(page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} files`
              }
            />
          </Col>
        </Row>
      </Card>

      {/* File Upload Modal - Modified to use finetuning prefix */}
      <FileUpload
        visible={uploadModalVisible}
        onClose={() => setUploadModalVisible(false)}
        bucket={selectedBucket}
        folderPrefix={FINETUNING_PREFIX}
        onUploadComplete={fetchFiles}
      />

      {/* File Preview Modal */}
      <FilePreview
        visible={previewModalVisible}
        onClose={() => {
          setPreviewModalVisible(false);
          setPreviewFile(null);
        }}
        file={previewFile}
      />

      {/* Create Bucket Modal */}
      <Modal
        title="Create New Bucket"
        open={bucketModalVisible}
        onCancel={() => setBucketModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          layout="vertical"
          onFinish={handleCreateBucket}
          initialValues={{ public: false }}
        >
          <Form.Item
            name="name"
            label="Bucket Name"
            rules={[
              { required: true, message: 'Please enter bucket name' },
              { pattern: /^[a-z0-9-]+$/, message: 'Bucket name can only contain lowercase letters, numbers, and hyphens' },
              { min: 3, message: 'Bucket name must be at least 3 characters' },
              { max: 63, message: 'Bucket name must be less than 63 characters' },
            ]}
          >
            <Input placeholder="e.g., my-finetuning-files" />
          </Form.Item>
          
          <Form.Item
            name="public"
            valuePropName="checked"
            label="Public Access"
          >
            <Checkbox>
              Allow public access to files in this bucket
            </Checkbox>
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setBucketModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Bucket
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainingFileStoragePage;
